import React from 'react';
import OrderDesign from '../../OrderDesign';

/**
 * Mobile Order Carousel Component
 * Positioned at bottom middle of screen between x=150 and x=791
 */
export const MobileOrderCarousel = ({
  you,
  yourTurn,
  hoveredIndex,
  setHoveredIndex,
  selectedOrderInstanceId,
  gameState,
  handleTouchDragStart,
  handleTouchDragMove,
  handleTouchDragEnd,
  handleCardTouch,
  playOrder
}) => {
  // Filter valid hand cards
  const validHand = (you.hand || []).filter(order => {
    if (order === null || order === undefined) return false;
    if (!order.instanceId) return false;
    const inCompleted = you.completedOrders?.some(co => co && co.instanceId === order.instanceId);
    if (inCompleted) return false;
    const inDeck = you.deck?.some(d => d && d.instanceId === order.instanceId);
    if (inDeck) return false;
    return true;
  });

  if (validHand.length === 0) return null;

  // Scale factor for mobile cards - adjust this single value to resize all cards
  const MOBILE_CARD_SCALE = 0.52;
  
  // Boundaries in grid coordinates: left=120, right=723
  const LEFT_BOUNDARY = 120;
  const AVAILABLE_WIDTH = 603; // 723 - 120
  
  // Card dimensions
  const CARD_BASE_WIDTH = 200;
  const CARD_BASE_HEIGHT = 100;
  const SCALED_CARD_WIDTH = CARD_BASE_WIDTH * MOBILE_CARD_SCALE; // 104px
  const SCALED_CARD_HEIGHT = CARD_BASE_HEIGHT * MOBILE_CARD_SCALE; // 52px
  
  const cardCount = validHand.length;
  
  // Calculate how much space each card can take (including overlap)
  // All cards must fit within AVAILABLE_WIDTH
  let cardSpacing;
  if (cardCount <= 1) {
    cardSpacing = 0;
  } else {
    // Total width = (cardCount - 1) * spacing + SCALED_CARD_WIDTH = AVAILABLE_WIDTH
    // spacing = (AVAILABLE_WIDTH - SCALED_CARD_WIDTH) / (cardCount - 1)
    cardSpacing = Math.min(
      SCALED_CARD_WIDTH + 4, // Max spacing (small gap, no overlap)
      (AVAILABLE_WIDTH - SCALED_CARD_WIDTH) / (cardCount - 1)
    );
  }
  
  // Calculate total width cards will occupy
  const totalCardsWidth = cardCount <= 1 
    ? SCALED_CARD_WIDTH 
    : (cardCount - 1) * cardSpacing + SCALED_CARD_WIDTH;
  
  // Center the cards within the available width
  const startOffset = (AVAILABLE_WIDTH - totalCardsWidth) / 2;

  return (
    <div 
      className="order-carousel-mobile-wrapper"
      style={{
        position: "absolute",
        bottom: "45px",
        left: `${LEFT_BOUNDARY}px`,
        width: `${AVAILABLE_WIDTH}px`,
        height: `${SCALED_CARD_HEIGHT}px`,
        zIndex: 1003,
        overflow: "visible" // Allow cards to extend above
      }}
    >
      {/* Cards positioned absolutely within the wrapper */}
        {validHand.map((order, idx) => {
          if (!order || !order.instanceId) return null;
          
          const isHovered = idx === hoveredIndex;
          const orderCost = order.hasInstanceModifications && order.instanceCost !== undefined 
            ? order.instanceCost 
            : order.cost;
          const isPlayable = yourTurn && you.intelligence >= orderCost;
          
          // Calculate this card's left position
          const cardLeft = startOffset + idx * cardSpacing;
          
          return (
            <div
              key={order.instanceId || `mobile-card-${idx}`}
              className={`carousel-order-card-mobile${isHovered ? " expanded" : ""}`}
              onTouchStart={(e) => {
                if (!yourTurn) return;
                setHoveredIndex(idx);
                handleTouchDragStart(e, order);
              }}
              onTouchMove={(e) => {
                if (!yourTurn) return;
                handleTouchDragMove(e);
              }}
              onTouchEnd={(e) => {
                if (!yourTurn) return;
                const { wasDragging, order: dragOrder, zone } = handleTouchDragEnd(e);
                setHoveredIndex(null);
                
                if (wasDragging) {
                  e.preventDefault();
                  e.stopPropagation();
                  if (dragOrder && zone) {
                    const isDeployableToZone = dragOrder.type !== 'Drone' && 
                                                dragOrder.type !== 'Strategem' && 
                                                dragOrder.type !== 'Weapon';
                    if (isDeployableToZone) {
                      playOrder(dragOrder.id, zone, null, dragOrder.instanceId);
                    } else {
                      handleCardTouch(dragOrder, e);
                    }
                  }
                } else {
                  e.preventDefault();
                  e.stopPropagation();
                  handleCardTouch(order, e);
                }
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleCardTouch(order, e);
              }}
              style={{
                position: "absolute",
                left: `${cardLeft}px`,
                bottom: 0,
                width: `${SCALED_CARD_WIDTH}px`,
                height: `${SCALED_CARD_HEIGHT}px`,
                cursor: "pointer",
                zIndex: isHovered ? 100 : idx
              }}
            >
              {/* Scaled card content - scale from bottom-left so bottom stays at wrapper bottom */}
              <div style={{
                width: `${CARD_BASE_WIDTH}px`,
                height: `${CARD_BASE_HEIGHT}px`,
                transform: `scale(${MOBILE_CARD_SCALE})`,
                transformOrigin: "bottom left",
                position: "absolute",
                bottom: 0,
                left: 0
              }}>
                <OrderDesign
                  order={order}
                  count={0}
                  minimal={true}
                  scale={0.5}
                  isOOR={!isPlayable && yourTurn}
                  isSelected={selectedOrderInstanceId === order.instanceId}
                  isPlayable={isPlayable}
                  gameState={gameState}
                />
              </div>
            </div>
          );
        })}
    </div>
  );
};

export default MobileOrderCarousel;

